<template>
  <div class="ai-page">
    <section class="ai-hero">
      <div class="label">
        <div>
          <span class="eyebrow">Cost telemetry</span>
          <h4>AI Usage &amp; Cost</h4>
        </div>
        <div class="hero-controls">
          <div class="window-switch">
            <button
              v-for="w in windowOptions"
              :key="w.hours"
              class="window-btn"
              :class="{ active: windowHours === w.hours }"
              @click="setWindow(w.hours)"
            >{{ w.label }}</button>
          </div>
          <button class="btn-refresh" @click="fetchAll()">Refresh</button>
        </div>
      </div>
      <p class="intro">
        Token usage and estimated USD cost for every AI provider call across scheduled jobs,
        over the selected window. Click a prompt or job row to filter the raw call log below.
      </p>
    </section>

    <div v-if="error" class="alert">{{ error }}</div>

    <div class="pricing-note">
      <span class="icon icon-info-with-circle"></span>
      <span>
        Costs are <strong>estimates</strong> — derived from server-side rates in
        <code>config/aiPricing.js</code> (notably <code>deepseek-v4-pro</code>). They are only as
        accurate as those configured rates.
      </span>
    </div>

    <!-- Totals -->
    <div class="kpi-row">
      <div class="kpi-card kpi-cost">
        <span class="kpi-label">Total cost</span>
        <span class="kpi-value">{{ fmtCost(totals.costUsd) }}</span>
        <span class="kpi-sub">{{ fmtCost(totals.avgCostPerCall) }} / call avg</span>
        <span class="kpi-sub">{{ costBreakdownText(totals) }}</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Total calls</span>
        <span class="kpi-value">{{ fmtNum(totals.calls) }}</span>
        <span class="kpi-sub">{{ fmtNum(totals.failures) }} failed</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Success rate</span>
        <span class="kpi-value" :class="pctClass(totals.successRate)">{{ fmtPct(totals.successRate) }}</span>
        <span class="kpi-sub">{{ fmtNum(totals.successes) }} ok</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Total tokens</span>
        <span class="kpi-value">{{ fmtNum(totals.totalTokens) }}</span>
        <span class="kpi-sub">{{ fmtNum(totals.inputTokens) }} in / {{ fmtNum(totals.outputTokens) }} out</span>
      </div>
    </div>

    <!-- By prompt -->
    <div class="section-head">
      <span class="eyebrow">Breakdown</span>
      <h5>By prompt</h5>
    </div>
    <AdminTable
      :columns="promptColumns"
      :rows="byPrompt"
      :row-key="'key'"
      :default-sort="{ key: 'cost', dir: 'desc' }"
      empty-text="No AI calls recorded in this window."
      clickable
      @row-click="filterByPrompt"
    >
      <template v-slot:cell-key="{ row }"><span class="key-cell">{{ row.key }}</span></template>
      <template v-slot:cell-calls="{ row }">{{ fmtNum(row.calls) }}</template>
      <template v-slot:cell-success="{ row }"><span :class="pctClass(row.successRate)">{{ fmtPct(row.successRate) }}</span></template>
      <template v-slot:cell-errors="{ row }"><span class="muted-sub">{{ errorCategorySummary(row) }}</span></template>
      <template v-slot:cell-intok="{ row }">{{ fmtNum(row.inputTokens) }}</template>
      <template v-slot:cell-outtok="{ row }">{{ fmtNum(row.outputTokens) }}</template>
      <template v-slot:cell-cost="{ row }">
        <span class="cost-cell">
          <strong>{{ fmtCost(costTotal(row)) }}</strong>
          <small>{{ costBreakdownText(row) }}</small>
          <small v-if="webSearchCount(row)">{{ fmtNum(webSearchCount(row)) }} web searches</small>
        </span>
      </template>
      <template v-slot:cell-avg="{ row }">{{ fmtCostPerCall(row) }}</template>
      <template v-slot:cell-jobs="{ row }">
        <span class="chips">
          <span v-for="j in (row.jobs || [])" :key="j" class="mini-chip">{{ j }}</span>
          <span v-if="!(row.jobs || []).length" class="muted-sub">—</span>
        </span>
      </template>
    </AdminTable>

    <!-- By job -->
    <div class="section-head">
      <span class="eyebrow">Breakdown</span>
      <h5>By job <span class="head-hint">avg / call = total cost / calls; avg / run = what one run costs</span></h5>
    </div>
    <AdminTable
      :columns="jobColumns"
      :rows="byJob"
      :row-key="'key'"
      :default-sort="{ key: 'cost', dir: 'desc' }"
      empty-text="No AI calls recorded in this window."
      clickable
      @row-click="filterByJob"
    >
      <template v-slot:cell-key="{ row }"><span class="key-cell">{{ row.key }}</span></template>
      <template v-slot:cell-runs="{ row }">{{ fmtNum(row.runs) }}</template>
      <template v-slot:cell-calls="{ row }">{{ fmtNum(row.calls) }}</template>
      <template v-slot:cell-success="{ row }"><span :class="pctClass(row.successRate)">{{ fmtPct(row.successRate) }}</span></template>
      <template v-slot:cell-errors="{ row }"><span class="muted-sub">{{ errorCategorySummary(row) }}</span></template>
      <template v-slot:cell-tokens="{ row }">{{ fmtNum(row.totalTokens) }}</template>
      <template v-slot:cell-cost="{ row }">
        <span class="cost-cell">
          <strong>{{ fmtCost(costTotal(row)) }}</strong>
          <small>{{ costBreakdownText(row) }}</small>
          <small v-if="webSearchCount(row)">{{ fmtNum(webSearchCount(row)) }} web searches</small>
        </span>
      </template>
      <template v-slot:cell-avgcall="{ row }"><span class="cost-run">{{ fmtCostPerCall(row) }}</span></template>
      <template v-slot:cell-avgrun="{ row }"><span class="cost-run">{{ fmtCost(row.avgCostPerRun) }}</span></template>
    </AdminTable>

    <!-- By model -->
    <div class="section-head">
      <span class="eyebrow">Breakdown</span>
      <h5>By model</h5>
    </div>
    <AdminTable
      :columns="modelColumns"
      :rows="byModel"
      :row-key="'key'"
      :default-sort="{ key: 'cost', dir: 'desc' }"
      empty-text="No AI calls recorded in this window."
    >
      <template v-slot:cell-key="{ row }"><span class="key-cell">{{ row.key }}</span></template>
      <template v-slot:cell-calls="{ row }">{{ fmtNum(row.calls) }}</template>
      <template v-slot:cell-success="{ row }"><span :class="pctClass(row.successRate)">{{ fmtPct(row.successRate) }}</span></template>
      <template v-slot:cell-tokens="{ row }">
        <span class="token-stack">
          <strong>{{ fmtNum(row.totalTokens) }}</strong>
          <small>{{ fmtNum(row.inputTokens) }} in / {{ fmtNum(row.outputTokens) }} out</small>
        </span>
      </template>
      <template v-slot:cell-cost="{ row }">
        <span class="cost-cell">
          <strong>{{ fmtCost(costTotal(row)) }}</strong>
          <small>{{ costBreakdownText(row) }}</small>
          <small v-if="webSearchCount(row)">{{ fmtNum(webSearchCount(row)) }} web searches</small>
        </span>
      </template>
      <template v-slot:cell-avgcall="{ row }"><span class="cost-run">{{ fmtCostPerCall(row) }}</span></template>
      <template v-slot:cell-rate="{ row }">
        <span class="rate-cell">
          <span>{{ fmtCost(rateFor(row.key).inputPerMillion) }}<span class="rate-unit"> /1M in</span></span>
          <span>{{ fmtCost(rateFor(row.key).outputPerMillion) }}<span class="rate-unit"> /1M out</span></span>
          <span v-if="webSearchCount(row) && webSearchRate(row)" class="tool-rate">
            web_search {{ fmtCost(webSearchRate(row).perThousand) }} /1K calls
          </span>
          <span v-if="rateFor(row.key).estimated" class="rate-est">
            {{ rateFor(row.key).fallback ? 'fallback est.' : 'estimated' }}
          </span>
          <span v-if="rateFor(row.key).note" class="rate-note">{{ rateFor(row.key).note }}</span>
        </span>
      </template>
    </AdminTable>

    <!-- Recent calls -->
    <div class="section-head">
      <span class="eyebrow">Raw log</span>
      <h5>Recent calls</h5>
    </div>
    <div class="event-filters">
      <span class="filter-chip" v-if="filters.promptKey">prompt: {{ filters.promptKey }}<a @click="clearFilter('promptKey')">x</a></span>
      <span class="filter-chip" v-if="filters.jobKey">job: {{ filters.jobKey }}<a @click="clearFilter('jobKey')">x</a></span>
      <span class="filter-chip" v-if="filters.errorCategory">error: {{ prettyLabel(filters.errorCategory) }}<a @click="clearFilter('errorCategory')">x</a></span>
      <select v-model="filters.errorCategory" class="error-filter" @change="setErrorCategoryFilter">
        <option value="">All error categories</option>
        <option v-for="category in errorCategoryOptions" :key="category" :value="category">
          {{ prettyLabel(category) }}
        </option>
      </select>
      <a v-if="filters.promptKey || filters.jobKey || filters.errorCategory" class="focus-clear" @click="clearEventFilters">clear all</a>
    </div>
    <AdminTable
      :columns="eventColumns"
      :rows="events"
      :row-key="'id'"
      :loading="eventsLoading"
      min-width="1240px"
      empty-text="No calls match these filters yet."
    >
      <template v-slot:cell-time="{ row }">
        <span :title="fmtTime(row.created_at)">{{ fmtRelative(row.created_at) }}</span>
      </template>
      <template v-slot:cell-prompt="{ row }">
        <a class="link-cell" :title="row.prompt_key" @click="filterByPrompt(row.prompt_key)">{{ row.prompt_key || '—' }}</a>
      </template>
      <template v-slot:cell-job="{ row }">
        <a class="link-cell" :title="row.job_key" @click="filterByJob(row.job_key)">{{ row.job_key || '—' }}</a>
      </template>
      <template v-slot:cell-run="{ row }"><span class="mono muted-sub">{{ shortId(row.job_run_id) }}</span></template>
      <template v-slot:cell-model="{ row }"><span class="mono">{{ modelLabel(row) }}</span></template>
      <template v-slot:cell-intok="{ row }">{{ fmtNum(row.input_tokens) }}</template>
      <template v-slot:cell-outtok="{ row }">{{ fmtNum(row.output_tokens) }}</template>
      <template v-slot:cell-cost="{ row }">
        <span class="cost-cell">
          <strong>{{ fmtCost(costTotal(row)) }}</strong>
          <small>{{ costBreakdownText(row) }}</small>
          <small v-if="webSearchCount(row)">{{ fmtNum(webSearchCount(row)) }} web searches</small>
        </span>
      </template>
      <template v-slot:cell-success="{ row }">
        <span class="dot-pill" :class="eventResultTone(row)">{{ eventResultLabel(row) }}</span>
      </template>
      <template v-slot:cell-error="{ row }"><span class="muted-sub">{{ row.error_category || '—' }}</span></template>
      <template v-slot:cell-duration="{ row }">{{ fmtDuration(row.duration_ms) }}</template>
    </AdminTable>

    <div class="pager" v-if="eventsTotal > 0">
      <span class="pager-info">Showing {{ eventsFrom }}–{{ eventsTo }} of {{ eventsTotal }}</span>
      <div class="pager-buttons">
        <button class="row-btn" :disabled="!eventsHasPrev || eventsLoading" @click="prevEvents">Previous</button>
        <button class="row-btn" :disabled="!eventsHasNext || eventsLoading" @click="nextEvents">Next</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'
import AdminTable from './shared/AdminTable.vue'

export default {
  name: 'AiUsage',
  components: { AdminTable },
  data () {
    return {
      windowHours: 720,
      windowOptions: [
        { hours: 24, label: '24h' },
        { hours: 168, label: '7d' },
        { hours: 720, label: '30d' }
      ],
      summary: null,
      pricing: {},
      toolPricing: {},
      events: [],
      eventsTotal: 0,
      eventsLimit: 50,
      eventsOffset: 0,
      filters: { promptKey: '', jobKey: '', errorCategory: '' },
      loading: false,
      eventsLoading: false,
      error: null
    }
  },
  computed: {
    totals () {
      return (this.summary && this.summary.totals) || {}
    },
    byPrompt () { return (this.summary && this.summary.byPrompt) || [] },
    byJob () { return (this.summary && this.summary.byJob) || [] },
    byModel () { return (this.summary && this.summary.byModel) || [] },
    errorCategoryOptions () {
      const found = {}
      const add = (value) => {
        if (value !== undefined && value !== null && value !== '') found[String(value)] = true
      }
      const summary = this.summary || {}
      ;['errorCategories', 'error_categories', 'byErrorCategory', 'by_error_category'].forEach((key) => {
        const value = summary[key]
        if (Array.isArray(value)) {
          value.forEach((row) => add(row && (row.error_category || row.errorCategory || row.key || row.category || row.name)))
        } else if (value && typeof value === 'object') {
          Object.keys(value).forEach(add)
        }
      })
      this.byPrompt.forEach((row) => this.errorCategoryEntries(row).forEach((entry) => add(entry.key)))
      this.byJob.forEach((row) => this.errorCategoryEntries(row).forEach((entry) => add(entry.key)))
      this.events.forEach((row) => add(row && (row.error_category || row.errorCategory)))
      return Object.keys(found).sort()
    },
    promptColumns () {
      return [
        { key: 'key', label: 'Prompt', width: '16%', breakable: true, sortable: true, sortAccessor: (r) => r.key },
        { key: 'calls', label: 'Calls', width: '7%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.calls) },
        { key: 'success', label: 'Success', width: '8%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.successRate) },
        { key: 'errors', label: 'Errors', width: '13%', breakable: true, sortable: true, sortAccessor: (r) => this.errorCategoryCount(r) },
        { key: 'intok', label: 'In tokens', width: '9%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.inputTokens) },
        { key: 'outtok', label: 'Out tokens', width: '9%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.outputTokens) },
        { key: 'cost', label: 'Total cost', width: '10%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(this.costTotal(r)) },
        { key: 'avg', label: 'Avg / call', width: '10%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(this.costPerCall(r)) },
        { key: 'jobs', label: 'Used by jobs', width: '18%', breakable: true }
      ]
    },
    jobColumns () {
      return [
        { key: 'key', label: 'Job', width: '18%', breakable: true, sortable: true, sortAccessor: (r) => r.key },
        { key: 'runs', label: 'Runs', width: '7%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.runs) },
        { key: 'calls', label: 'Calls', width: '7%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.calls) },
        { key: 'success', label: 'Success', width: '9%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.successRate) },
        { key: 'errors', label: 'Errors', width: '12%', breakable: true, sortable: true, sortAccessor: (r) => this.errorCategoryCount(r) },
        { key: 'tokens', label: 'Tokens', width: '11%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.totalTokens) },
        { key: 'cost', label: 'Total cost', width: '12%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(this.costTotal(r)) },
        { key: 'avgcall', label: 'Avg / call', width: '11%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(this.costPerCall(r)) },
        { key: 'avgrun', label: 'Avg / run', width: '13%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.avgCostPerRun) }
      ]
    },
    modelColumns () {
      return [
        { key: 'key', label: 'Provider : model', width: '22%', breakable: true, sortable: true, sortAccessor: (r) => r.key },
        { key: 'calls', label: 'Calls', width: '8%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.calls) },
        { key: 'success', label: 'Success', width: '10%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.successRate) },
        { key: 'tokens', label: 'Token split', width: '15%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.totalTokens) },
        { key: 'cost', label: 'Total cost', width: '15%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(this.costTotal(r)) },
        { key: 'avgcall', label: 'Avg / call', width: '12%', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(this.costPerCall(r)) },
        { key: 'rate', label: 'Rate', width: '18%', breakable: true }
      ]
    },
    eventColumns () {
      return [
        { key: 'time', label: 'Time', width: '150px', nowrap: true, sortable: true, sortAccessor: (r) => new Date(r.created_at).getTime() || 0 },
        { key: 'prompt', label: 'Prompt', width: '150px', breakable: true, sortable: true, sortAccessor: (r) => r.prompt_key || '' },
        { key: 'job', label: 'Job', width: '155px', breakable: true, sortable: true, sortAccessor: (r) => r.job_key || '' },
        { key: 'run', label: 'Run', width: '70px', nowrap: true },
        { key: 'model', label: 'Model', width: '140px', breakable: true, sortable: true, sortAccessor: (r) => this.modelLabel(r) },
        { key: 'intok', label: 'In', width: '80px', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.input_tokens) },
        { key: 'outtok', label: 'Out', width: '80px', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.output_tokens) },
        { key: 'cost', label: 'Cost', width: '110px', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(this.costTotal(r)) },
        { key: 'success', label: 'Result', width: '75px', nowrap: true, sortable: true, sortAccessor: (r) => (this.truthy(r.success) ? 1 : 0) },
        { key: 'error', label: 'Error', width: '105px', breakable: true },
        { key: 'duration', label: 'Took', width: '80px', align: 'right', nowrap: true, sortable: true, sortAccessor: (r) => num(r.duration_ms) }
      ]
    },
    eventsFrom () { return this.eventsTotal === 0 ? 0 : this.eventsOffset + 1 },
    eventsTo () { return Math.min(this.eventsOffset + this.eventsLimit, this.eventsTotal) },
    eventsHasPrev () { return this.eventsOffset > 0 },
    eventsHasNext () { return this.eventsOffset + this.eventsLimit < this.eventsTotal }
  },
  mounted () {
    this.fetchAll()
  },
  methods: {
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    async fetchAll () {
      await Promise.all([this.fetchSummary(), this.fetchEvents()])
    },
    async fetchSummary () {
      this.loading = true
      try {
        const { data } = await axios.get(
          `${apiDomain}admin/ai-usage/summary?windowHours=${this.windowHours}`,
          { headers: this.authHeaders() })
        this.summary = data
        this.pricing = (data && data.pricing) || {}
        this.toolPricing = (data && data.toolPricing) || {}
        this.error = null
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.loading = false
      }
    },
    async fetchEvents () {
      this.eventsLoading = true
      try {
        const p = new URLSearchParams()
        p.set('limit', String(this.eventsLimit))
        p.set('offset', String(this.eventsOffset))
        if (this.filters.promptKey) p.set('promptKey', this.filters.promptKey)
        if (this.filters.jobKey) p.set('jobKey', this.filters.jobKey)
        if (this.filters.errorCategory) p.set('errorCategory', this.filters.errorCategory)
        const { data } = await axios.get(
          `${apiDomain}admin/ai-usage/events?${p.toString()}`,
          { headers: this.authHeaders() })
        this.events = Array.isArray(data.events) ? data.events : []
        const total = data.total
        this.eventsTotal = total != null ? Number(total) : this.events.length
      } catch (e) {
        // Don't clobber a summary 403 message; events errors are non-fatal here.
        if (!this.error) this.error = this.errMsg(e)
        this.events = []
      } finally {
        this.eventsLoading = false
      }
    },
    setWindow (hours) {
      if (this.windowHours === hours) return
      this.windowHours = hours
      this.fetchSummary()
    },
    filterByPrompt (rowOrKey) {
      const key = typeof rowOrKey === 'string' ? rowOrKey : (rowOrKey && rowOrKey.key)
      if (!key) return
      this.filters.promptKey = key
      this.eventsOffset = 0
      this.fetchEvents()
      this.scrollToLog()
    },
    filterByJob (rowOrKey) {
      const key = typeof rowOrKey === 'string' ? rowOrKey : (rowOrKey && rowOrKey.key)
      if (!key) return
      this.filters.jobKey = key
      this.eventsOffset = 0
      this.fetchEvents()
      this.scrollToLog()
    },
    clearFilter (which) {
      this.filters[which] = ''
      this.eventsOffset = 0
      this.fetchEvents()
    },
    setErrorCategoryFilter () {
      this.eventsOffset = 0
      this.fetchEvents()
      this.scrollToLog()
    },
    clearEventFilters () {
      this.filters = { promptKey: '', jobKey: '', errorCategory: '' }
      this.eventsOffset = 0
      this.fetchEvents()
    },
    scrollToLog () {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.event-filters') || this.$el.querySelector('.pager')
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    prevEvents () {
      if (!this.eventsHasPrev) return
      this.eventsOffset = Math.max(0, this.eventsOffset - this.eventsLimit)
      this.fetchEvents()
    },
    nextEvents () {
      if (!this.eventsHasNext) return
      this.eventsOffset += this.eventsLimit
      this.fetchEvents()
    },
    rateFor (key) {
      const exact = this.pricing && this.pricing[key]
      const entry = exact || (this.pricing && (this.pricing.default || this.pricing.fallback)) || {}
      const note = String(entry.note || entry.notes || '')
      return {
        inputPerMillion: num(entry.inputPerMillion || entry.input_per_million || entry.inputUsdPerMillion || entry.input_usd_per_million),
        outputPerMillion: num(entry.outputPerMillion || entry.output_per_million || entry.outputUsdPerMillion || entry.output_usd_per_million),
        estimated: !exact || /estimate|verify|fallback/i.test(note),
        fallback: !exact || /fallback/i.test(note),
        note
      }
    },
    pickValue (row, keys) {
      if (!row) return null
      for (const key of keys) {
        if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key]
      }
      const breakdown = row.costBreakdown || row.cost_breakdown || {}
      for (const key of keys) {
        if (breakdown[key] !== undefined && breakdown[key] !== null && breakdown[key] !== '') return breakdown[key]
      }
      return null
    },
    costTotal (row) {
      const value = this.pickValue(row, ['totalCostUsd', 'total_cost_usd', 'costUsd', 'cost_usd', 'cost'])
      return value !== null ? value : null
    },
    costPerCall (row) {
      const reported = this.pickValue(row, ['avgCostPerCall', 'avg_cost_per_call'])
      if (reported !== null && Number.isFinite(Number(reported))) return Number(reported)

      const calls = Number(row && row.calls)
      const totalValue = this.costTotal(row)
      if (totalValue === null) return null
      const total = Number(totalValue)
      if (!(calls > 0) || !Number.isFinite(total)) return null
      return total / calls
    },
    tokenCost (row) {
      return this.pickValue(row, ['tokenCostUsd', 'token_cost_usd'])
    },
    toolCost (row) {
      return this.pickValue(row, ['toolCostUsd', 'tool_cost_usd'])
    },
    webSearchCount (row) {
      return num(this.pickValue(row, ['webSearchCalls', 'web_search_calls']))
    },
    costBreakdownText (row) {
      const token = this.tokenCost(row)
      const tool = this.toolCost(row)
      const parts = []
      if (token !== null) parts.push(`tokens ${this.fmtCost(token)}`)
      if (tool !== null) parts.push(`tools ${this.fmtCost(tool)}`)
      return parts.length ? parts.join(' + ') : 'tokens/tools not reported'
    },
    webSearchRate (row) {
      const key = row && row.key ? String(row.key) : ''
      const provider = (row && row.provider) || key.split(':')[0] || 'openai'
      const sources = [
        this.toolPricing && this.toolPricing[provider] && (this.toolPricing[provider].web_search || this.toolPricing[provider].webSearch),
        this.toolPricing && (this.toolPricing.web_search || this.toolPricing.webSearch),
        this.toolPricing && this.toolPricing.openai && (this.toolPricing.openai.web_search || this.toolPricing.openai.webSearch)
      ].filter(Boolean)
      const entry = sources[0]
      if (!entry) return null
      const rate = typeof entry === 'number'
        ? entry
        : (entry.perThousand || entry.per1KCalls || entry.per_1k_calls || entry.costPer1KCalls || entry.cost_per_1k_calls || entry.usdPer1KCalls || entry.usd_per_1k_calls || entry.pricePer1KCalls || entry.price_per_1k_calls)
      const n = Number(rate)
      if (isNaN(n)) return null
      return { perThousand: n, note: entry.note || entry.notes || '' }
    },
    modelLabel (e) {
      return [e.provider, e.model].filter(Boolean).join(':') || (e.model || '—')
    },
    errorCategoryEntries (row) {
      const raw = row && (row.errorCategories || row.error_categories || row.errorsByCategory || row.errors_by_category || row.byErrorCategory || row.by_error_category)
      if (!raw) return []
      if (Array.isArray(raw)) {
        return raw
          .map((entry) => ({
            key: entry && (entry.error_category || entry.errorCategory || entry.key || entry.category || entry.name),
            count: num(entry && (entry.count || entry.calls || entry.total || entry.value))
          }))
          .filter((entry) => entry.key)
      }
      if (typeof raw === 'object') {
        return Object.keys(raw)
          .map((key) => ({ key, count: num(raw[key]) }))
          .filter((entry) => entry.key)
      }
      return []
    },
    errorCategoryCount (row) {
      return this.errorCategoryEntries(row).reduce((sum, entry) => sum + num(entry.count), 0)
    },
    errorCategorySummary (row) {
      const entries = this.errorCategoryEntries(row)
        .sort((a, b) => b.count - a.count)
        .slice(0, 2)
      if (!entries.length) return '—'
      const suffix = this.errorCategoryEntries(row).length > 2 ? '…' : ''
      return entries.map((entry) => `${this.prettyLabel(entry.key)} ${this.fmtNum(entry.count)}`).join(', ') + suffix
    },
    eventAiResult (row) {
      return String((row && (row.ai_result || row.aiResult || row.result || row.output_result || row.outputResult)) || '').toLowerCase()
    },
    eventResultTone (row) {
      if (!this.truthy(row && row.success)) return 'bad'
      const result = this.eventAiResult(row)
      if (result === 'empty' || result.indexOf('empty') >= 0 || result.indexOf('items:0') >= 0) return 'warn'
      return 'ok'
    },
    eventResultLabel (row) {
      if (!this.truthy(row && row.success)) return 'Fail'
      return this.eventResultTone(row) === 'warn' ? 'Empty' : 'OK'
    },
    prettyLabel (value) {
      return String(value || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase())
    },
    // ---- formatting ----
    fmtCost (v) {
      const n = Number(v)
      if (isNaN(n)) return '—'
      if (n === 0) return '$0.00'
      return n < 0.01 ? '$' + n.toFixed(6) : '$' + n.toFixed(2)
    },
    fmtCostPerCall (row) {
      const value = this.costPerCall(row)
      if (value === null) return '\u2014'
      if (value === 0) return '$0.00'
      if (value < 0.01) return '$' + value.toFixed(6)
      if (value < 1) return '$' + value.toFixed(4)
      return '$' + value.toFixed(2)
    },
    fmtNum (v) {
      const n = Number(v)
      if (isNaN(n)) return '—'
      return n.toLocaleString('en-US')
    },
    fmtPct (rate) {
      const n = Number(rate)
      if (isNaN(n)) return '—'
      return (n * 100).toFixed(1) + '%'
    },
    pctClass (rate) {
      const n = Number(rate)
      if (isNaN(n)) return ''
      if (n < 0.8) return 'pct-bad'
      if (n < 0.95) return 'pct-warn'
      return 'pct-ok'
    },
    fmtDuration (ms) {
      const n = Number(ms)
      if (isNaN(n) || n <= 0) return '—'
      if (n < 1000) return Math.round(n) + 'ms'
      return (n / 1000).toFixed(1) + 's'
    },
    shortId (id) {
      const s = String(id == null ? '' : id)
      if (!s) return '—'
      return s.length > 8 ? s.slice(0, 6) + '…' : s
    },
    truthy (v) {
      return v === true || v === 1 || v === '1' || v === 'true'
    },
    fmtTime (t) {
      if (!t) return '—'
      const d = new Date(t)
      return isNaN(d) ? '—' : d.toLocaleString()
    },
    fmtRelative (t) {
      if (!t) return '—'
      const d = new Date(t)
      if (isNaN(d)) return '—'
      const s = Math.round((Date.now() - d.getTime()) / 1000)
      if (s < 0) return this.fmtTime(t)
      if (s < 45) return 'just now'
      const m = Math.round(s / 60)
      if (m < 60) return `${m}m ago`
      const h = Math.round(m / 60)
      if (h < 24) return `${h}h ago`
      const days = Math.round(h / 24)
      if (days < 7) return `${days}d ago`
      return this.fmtTime(t)
    },
    errMsg (e) {
      if (e && e.response && e.response.status === 403) {
        return 'Admin access required — you don’t have permission to view AI usage.'
      }
      return (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || 'Request failed'
    }
  }
}

// Module-scoped numeric coercion shared by column sort accessors.
function num (v) {
  const n = Number(v)
  return isNaN(n) ? 0 : n
}
</script>

<style scoped>
.ai-page {
  padding: 4px 8px 48px;
  color: #cfd2da;
}


.label {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid rgba(67, 72, 87, 0.9);
}

.label h4 {
  margin: 0 0 6px;
  color: #d8dde8;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.intro {
  max-width: 780px;
  margin: 0.6rem 0 1rem;
  color: #a4acbc;
  font-size: 14px;
  line-height: 1.55;
}

.hero-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 6px;
}

.window-switch {
  display: inline-flex;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 7px;
  overflow: hidden;
}

.window-btn {
  padding: 6px 12px;
  border: 0;
  background: rgba(20, 25, 35, 0.7);
  color: #9aa3b4;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}

.window-btn + .window-btn {
  border-left: 1px solid rgba(67, 72, 87, 0.9);
}

.window-btn.active {
  background: rgba(25, 151, 198, 0.16);
  color: #1fb2e8;
}

.btn-refresh {
  min-width: 84px;
  height: 32px;
  border: 1px solid rgba(25, 151, 198, 0.8);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.1);
  color: #1fb2e8;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  cursor: pointer;
}

.btn-refresh:hover {
  border-color: #1fb2e8;
  background: rgba(25, 151, 198, 0.2);
  color: #eaf7fc;
}

.alert {
  margin: 12px 0;
  padding: 10px 12px;
  border: 1px solid rgba(232, 79, 106, 0.5);
  border-radius: 8px;
  background: rgba(90, 34, 48, 0.48);
  color: #f3c6cf;
  font-size: 13px;
}

.pricing-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 0 6px;
  padding: 9px 12px;
  border: 1px solid rgba(224, 192, 126, 0.32);
  border-radius: 8px;
  background: rgba(224, 192, 126, 0.07);
  color: #ddd0a6;
  font-size: 12.5px;
  line-height: 1.5;
}

.pricing-note .icon { color: #e0c07e; margin-top: 1px; }
.pricing-note code {
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(58, 63, 77, 0.5);
  color: #e7d9ab;
  font-size: 11.5px;
}

/* Totals */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin: 16px 0 8px;
}

.kpi-card {
  display: grid;
  gap: 5px;
  padding: 16px;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.08), transparent 38%),
    rgba(14, 18, 27, 0.78);
}

.kpi-card.kpi-cost {
  border-color: rgba(27, 201, 142, 0.4);
}

.kpi-label {
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.kpi-value {
  color: #eaf0f7;
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
}

.kpi-cost .kpi-value { color: #6fe9c0; }

.kpi-sub {
  color: #7f899b;
  font-size: 11.5px;
}

/* Section heads */
.section-head {
  margin: 26px 0 10px;
}

.section-head h5 {
  margin: 2px 0 0;
  color: #d8dde8;
  font-size: 15px;
  font-weight: 700;
}

.head-hint {
  margin-left: 8px;
  color: #7f899b;
  font-size: 11.5px;
  font-weight: 500;
  text-transform: none;
}

/* Cells */
.key-cell {
  color: #dce1eb;
  font-weight: 600;
}

.cost-strong {
  color: #eaf0f7;
  font-weight: 700;
}

.cost-cell,
.token-stack {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: 0;
}

.cost-cell strong,
.token-stack strong {
  color: #eaf0f7;
  font-weight: 800;
  line-height: 1.1;
}

.cost-cell small,
.token-stack small {
  max-width: 170px;
  color: #7f899b;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1.25;
  white-space: normal;
}

.cost-run {
  color: #6fe9c0;
  font-weight: 800;
}

.pct-ok { color: #89dfbd; }
.pct-warn { color: #e0c07e; }
.pct-bad { color: #f0a1af; }

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.mini-chip {
  padding: 1px 7px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 6px;
  background: rgba(58, 63, 77, 0.3);
  font-size: 10.5px;
  color: #aeb5c5;
}

.rate-cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
  color: #c9ced9;
  font-size: 11.5px;
}

.rate-unit { color: #7f899b; }

.tool-rate,
.rate-note {
  color: #8e98ad;
  font-size: 10.5px;
  line-height: 1.25;
}

.tool-rate {
  color: #9ad9ef;
}

.rate-est {
  align-self: flex-start;
  margin-top: 2px;
  padding: 0 5px;
  border-radius: 6px;
  background: rgba(224, 192, 126, 0.12);
  color: #e0c07e;
  font-size: 9.5px;
  font-weight: 800;
  text-transform: uppercase;
}

.mono { font-family: monospace; }
.muted-sub { color: #7f899b; font-size: 11px; }

.link-cell {
  color: #7bccee;
  cursor: pointer;
}
.link-cell:hover { text-decoration: underline; }

.dot-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  border: 1px solid rgba(117, 124, 146, 0.3);
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}
.dot-pill.ok {
  border-color: rgba(27, 201, 142, 0.42);
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
}
.dot-pill.bad {
  border-color: rgba(232, 79, 106, 0.44);
  background: rgba(232, 79, 106, 0.12);
  color: #f0a1af;
}
.dot-pill.warn {
  border-color: rgba(224, 192, 126, 0.44);
  background: rgba(224, 192, 126, 0.12);
  color: #e0c07e;
}

/* Event filters + pager */
.event-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.error-filter {
  height: 30px;
  min-width: 176px;
  padding: 0 10px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 7px;
  background: rgba(8, 12, 20, 0.82);
  color: #cfd2da;
  font-size: 12px;
  outline: none;
}

.error-filter:focus {
  border-color: rgba(25, 151, 198, 0.78);
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.12);
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border: 1px solid rgba(25, 151, 198, 0.36);
  border-radius: 999px;
  background: rgba(25, 151, 198, 0.08);
  color: #bfe6f5;
  font-size: 12px;
}

.filter-chip a {
  color: #7bccee;
  cursor: pointer;
  font-weight: 800;
}

.focus-clear {
  color: #1fb2e8;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
}

.pager-info { color: #8f98aa; font-size: 12px; }

.row-btn {
  height: 27px;
  margin-left: 8px;
  padding: 0 11px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(20, 25, 35, 0.7);
  color: #cfd2da;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 700;
}

.row-btn:hover:not(:disabled) {
  border-color: rgba(25, 151, 198, 0.7);
  color: #eaf7fc;
}

.row-btn:disabled { opacity: 0.5; cursor: default; }

@media screen and (max-width: 1100px) {
  .kpi-row { grid-template-columns: repeat(2, 1fr); }
}
</style>
